import { Box, Button, Typography } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ActionBtn from "../../../components/activity/ActionBtn";
import AddImageComponent from "../../../components/activity/AddImageComponent";
import StepsEditor from "../../../components/activity/StepsEditor";
import Layout from "../../../components/layout/Layout";
import LoadingModal from "../../../components/modals/LoadingModal";
import { useActivities } from "../../../state/ActivityContext";
import { ClientContext } from "../../../state/ClientContext";
import {
  MESSAGE_ACTIVITY_NO_PAST_START,
  MESSAGE_ACTIVITY_NO_PAST_START_TIME,
} from "../../../utils/activityTimezone";
import "./AddActivityPage.css";
import ActivityBasicDetails from "./components/ActivityBasicDetails";
import ActivityDateTime from "./components/ActivityDateTime";
import ActivitySettings from "./components/ActivitySettings";
import { useActivityForm } from "./useActivityForm";

const AddActivityPage = () => {
  const { selectedClient } = useContext(ClientContext);
  const { selectedActivity, isLoading, getActivityStatus } = useActivities();
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedClient) {
      navigate("/select-client-homepage", { replace: true });
    }
  }, [selectedClient, navigate]);

  const {
    formik,
    editingActivity,
    showLoadingModal,
    handleStepChange,
    addStep,
    handleStepImageChange,
    handleStepImageDelete,
    handleDeleteStep,
    handleActivityImageChange,
    handleActivityImageDelete,
    videoUrlWarning,
    markRepeatEndUserOverride,
    clientMinWallDate,
    activityClientIanaZone,
  } = useActivityForm();

  const resolvedActivity = selectedActivity || editingActivity;

  const getMode = () => {
    if (!resolvedActivity) return "new";
    const hasStarted = getActivityStatus(resolvedActivity)[0];
    return hasStarted ? "view" : "edit";
  };
  const mode = getMode();
  const eventState = resolvedActivity
    ? getActivityStatus(resolvedActivity)[1]
    : "";

  const handleClose = () => {
    navigate(`/calendar/${selectedClient?.id}`);
  };

  const getStatusDisplay = () => {
    switch (eventState) {
      case "completed":
        return {
          text: "Completed",
          color: "#4CAF50", // Green
        };
      case "incomplete":
        return {
          text: "Incomplete",
          color: "#F44336", // Red
        };
      case "in_progress":
        return {
          text: "In progress",
          color: "#FF9800", // Orange
        };
      default:
        return {
          text: "Not started",
          color: "#757575", // Gray
        };
    }
  };

  return (
    <Layout className="bg-black" showSideNav={false} isTouched={formik.dirty}>
      <LoadingModal isOpen={showLoadingModal} />
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            padding: 4,
            width: "840px",
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mb: 3,
              fontWeight: "bold",
              color: "white",
              textAlign: "start",
            }}
          >
            {mode === "new" ? "Add New" : mode === "edit" ? "Edit" : "View"}{" "}
            Activity
          </Typography>
          {!isLoading && (
            <Box
              sx={{
                backgroundColor: "#D9D9D9",
                padding: 4,
                borderRadius: 2,
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box>
                <h5 style={{ textAlign: "left" }}>Activity details</h5>
                <ActivityBasicDetails
                  formik={formik}
                  mode={mode}
                  videoUrlWarning={videoUrlWarning}
                />
                <ActivityDateTime
                  formik={formik}
                  mode={mode}
                  selectedActivity={resolvedActivity}
                  onRepeatEndUserOverride={markRepeatEndUserOverride}
                  clientMinWallDate={clientMinWallDate}
                  clientIanaZone={activityClientIanaZone}
                />
                <AddImageComponent
                  inputName="activity_image"
                  fileName={formik.values?.activity_image}
                  onImageChange={handleActivityImageChange}
                  onImageDelete={handleActivityImageDelete}
                />
                {formik.status?.error && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {formik.status.error}
                  </Typography>
                )}
              </Box>

              <Box sx={{ padding: 2 }}>
                <ActivitySettings formik={formik} />
              </Box>

              <StepsEditor
                steps={formik.values.steps}
                mode={mode}
                onStepChange={handleStepChange}
                onDeleteStep={handleDeleteStep}
                onAddStep={addStep}
                onStepImageChange={handleStepImageChange}
                onStepImageDelete={handleStepImageDelete}
                errors={formik.errors.steps}
                selectedActivity={resolvedActivity}
              />

              {mode === "view" && (
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: "#FFFFFF",
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Status:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: getStatusDisplay().color,
                      fontWeight: 500,
                    }}
                  >
                    {getStatusDisplay().text}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mb: 4,
            mx: 3,
            width: "calc(100% - 24px)",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          {formik.submitCount > 0 && formik.errors.date_from && (
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "0.875rem",
                maxWidth: "420px",
                textAlign: "right",
                lineHeight: 1.35,
              }}
              role="alert"
              id="activity-save-blocked-date-from"
              data-yup-rule={
                formik.errors.date_from === MESSAGE_ACTIVITY_NO_PAST_START
                  ? "no-past-start-day"
                  : undefined
              }
            >
              {formik.errors.date_from}
            </Typography>
          )}
          {formik.submitCount > 0 && formik.errors.start_time && (
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "0.875rem",
                maxWidth: "420px",
                textAlign: "right",
                lineHeight: 1.35,
              }}
              role="alert"
              id="activity-save-blocked-start-time"
              data-yup-rule={
                formik.errors.start_time === MESSAGE_ACTIVITY_NO_PAST_START_TIME
                  ? "no-past-start-instant-on-today"
                  : undefined
              }
            >
              {formik.errors.start_time}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {mode === "view" ? (
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                bgcolor: "#707070",
                color: "#fff",
                "&:hover": { bgcolor: "#5a5a5a" },
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Close
            </Button>
          ) : (
            <ActionBtn
              handleSave={formik.handleSubmit}
              actionMode={
                mode === "new" ? "Add New" : mode === "edit" ? "Edit" : "View"
              }
              isSaving={isLoading}
              enableDeleteOptions={
                formik.values.repeat_type !== "none" ? true : false
              }
            />
          )}
          </Box>
        </Box>
      </LocalizationProvider>
    </Layout>
  );
};

export default AddActivityPage;
